/**
 * /app/$tenantSlug/reports/analytics — HR analytics dashboard (G19).
 *
 * Moved here from Settings → Organization → Analytics (persona round
 * 2026-10: a report is not a setting). The old /settings/analytics URL
 * redirects here so bookmarks keep working.
 *
 * HR-admin only — the page hard role-gates to match the backend RBAC
 * (RequireRole(company_admin, hr_manager) on /analytics/dashboard).
 *
 * Window: defaults to "1 April of the current financial year → today"
 * with monthly buckets.
 *
 * Six KPI sections (headcount / attrition / leave / training / helpdesk /
 * surveys), each in its own card. The backend computes sections
 * independently and lists failures in `errors[]`; a failed section shows
 * an inline notice instead of hiding the whole page.
 *
 * The response types in lib/api/analytics.ts mirror the Go structs key for
 * key — the old page read keys the server never sent, so training always
 * showed 0% and approved leave never appeared.
 *
 * Charting: pure-CSS bars (no chart library).
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BarChart2,
  GraduationCap,
  HeartHandshake,
  LifeBuoy,
  TrendingDown,
  Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { analyticsApi } from "@/lib/api";
import type {
  AnalyticsBucket,
  AnalyticsDashboardResponse,
  AnalyticsSectionKey,
} from "@/lib/api/analytics";
import { todayISO } from "@/lib/leave-format";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/reports/analytics",
)({
  component: AnalyticsDashboardPage,
});

/** Typed loosely so the link compiles before the Reports index exists. */
const REPORTS_HREF: string = "/app/$tenantSlug/reports";

const SECTION_KEYS: readonly AnalyticsSectionKey[] = [
  "headcount",
  "attrition",
  "leave_heatmap",
  "training_compliance",
  "helpdesk_sla",
  "survey_participation",
];

const REASON_LABELS: Record<string, string> = {
  resignation: "Resignation",
  termination: "Termination",
  end_of_contract: "End of Contract",
  retirement: "Retirement",
  abandonment: "Absconding",
  death: "Death",
};

const MONTH_SHORT = new Intl.DateTimeFormat("en-GB", {
  month: "short",
  timeZone: "UTC",
});

/** YYYY-MM-DD for 1 April of the current Indian financial year. */
function fyStart(): string {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-04-01`;
}

function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

/** Colour-coded eNPS: green ≥ 50, amber 0..49, red < 0. */
function enpsClass(score: number): string {
  if (score >= 50) return "text-emerald-700 dark:text-emerald-400";
  if (score >= 0) return "text-amber-600";
  return "text-red-600";
}

function netDeltaClass(delta: number): string {
  if (delta > 0) return "text-emerald-700 dark:text-emerald-400";
  if (delta < 0) return "text-red-600";
  return "text-muted-foreground";
}

interface SectionCardProps {
  title: string;
  icon: typeof BarChart2;
  description?: string;
  href?: string;
  hrefLabel?: string;
  hasError?: boolean;
  children: React.ReactNode;
}

function SectionCard({
  title,
  icon: Icon,
  description,
  href,
  hrefLabel = "Open",
  hasError,
  children,
}: Readonly<SectionCardProps>) {
  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent text-accent-foreground">
              <Icon className="h-4 w-4" />
            </span>
            <div>
              <CardTitle className="text-base">{title}</CardTitle>
              {description && (
                <CardDescription className="mt-0.5 text-xs">
                  {description}
                </CardDescription>
              )}
            </div>
          </div>
          {href && (
            <Link
              to={href}
              className="relative inline-flex shrink-0 items-center gap-1 text-xs text-primary hover:underline pointer-coarse:after:absolute pointer-coarse:after:-inset-3.5"
            >
              {hrefLabel}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 pt-0">
        {hasError ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>
              This section couldn't load. Try again in a few minutes or pick
              a shorter date range.
            </p>
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

/** A label + a bar whose width is value/max. */
function CSSBarRow({
  label,
  value,
  max,
  formatted,
  tone = "primary",
}: Readonly<{
  label: string;
  value: number;
  max: number;
  formatted: string;
  tone?: "primary" | "warn";
}>) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 py-1 text-xs">
      <span className="w-28 shrink-0 truncate text-muted-foreground" title={label}>
        {label}
      </span>
      <div className="flex h-2 flex-1 overflow-hidden rounded bg-muted">
        <div
          className={cn(
            "h-full rounded",
            tone === "warn" ? "bg-amber-500" : "bg-primary",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-12 shrink-0 text-right tabular-nums text-foreground">
        {formatted}
      </span>
    </div>
  );
}

/** A row of equal-width vertical bars. */
function CSSColumns({
  data,
  highlight,
}: Readonly<{
  data: Array<{ key: string; label: string; value: number; title?: string }>;
  highlight?: number;
}>) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="flex items-end gap-1.5">
      {data.map((d, i) => {
        const pct = d.value > 0 ? Math.max(4, Math.round((d.value / max) * 100)) : 0;
        return (
          <div
            key={d.key}
            className="flex min-w-0 flex-1 flex-col items-center gap-1"
            title={d.title ?? `${d.label}: ${d.value}`}
          >
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {d.value > 0 ? d.value : ""}
            </span>
            <div className="flex h-20 w-full items-end rounded-t bg-muted/40">
              <div
                className={cn(
                  "w-full rounded-t",
                  i === highlight ? "bg-primary" : "bg-primary/60",
                )}
                style={{ height: `${pct}%` }}
              />
            </div>
            <span className="truncate text-[10px] text-muted-foreground">
              {d.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function HeadcountSectionView({
  data,
  hasError,
}: Readonly<{
  data: AnalyticsDashboardResponse["headcount"];
  hasError: boolean;
}>) {
  const buckets = data?.buckets ?? [];
  const delta = data?.net_change ?? 0;
  return (
    <SectionCard
      title="Headcount"
      icon={Users}
      description="Active employees (joined by the end date), joiners and leavers"
      href="/app/$tenantSlug/people"
      hrefLabel="People"
      hasError={hasError}
    >
      <div className="mb-3 flex items-baseline gap-3">
        <p className="text-3xl font-semibold tabular-nums">
          {data?.active_as_of_to ?? 0}
        </p>
        <p className={cn("text-sm tabular-nums", netDeltaClass(delta))}>
          {delta > 0 ? "+" : ""}
          {delta} since start
        </p>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        {data?.new_hires ?? 0} joined · {data?.exits ?? 0} left
      </p>
      {buckets.length > 0 ? (
        <CSSColumns
          data={buckets.map((b) => ({
            key: b.bucket_start,
            label: MONTH_SHORT.format(new Date(b.bucket_start)),
            value: b.new_hires,
            title: `${MONTH_SHORT.format(new Date(b.bucket_start))}: ${b.new_hires} joined, ${b.exits} left`,
          }))}
        />
      ) : (
        <p className="text-xs text-muted-foreground">No joiners or leavers in this period.</p>
      )}
    </SectionCard>
  );
}

function AttritionSectionView({
  data,
  hasError,
}: Readonly<{
  data: AnalyticsDashboardResponse["attrition"];
  hasError: boolean;
}>) {
  const reasons = data?.by_reason ?? [];
  const max = Math.max(1, ...reasons.map((r) => r.count));
  return (
    <SectionCard
      title="Attrition"
      icon={TrendingDown}
      description="Resignations as a share of average headcount"
      href="/app/$tenantSlug/hr/offboarding"
      hrefLabel="Exits"
      hasError={hasError}
    >
      <div className="mb-3">
        <p className="text-3xl font-semibold tabular-nums">
          {data ? formatBps(data.voluntary_bps) : "0.00%"}
        </p>
        <p className="text-xs text-muted-foreground">
          {data?.voluntary_exits ?? 0} resigned · {data?.other_exits ?? 0} other exits
        </p>
      </div>
      {reasons.length > 0 ? (
        <div className="space-y-0.5">
          {reasons.slice(0, 5).map((r) => (
            <CSSBarRow
              key={r.reason}
              label={REASON_LABELS[r.reason] ?? r.reason}
              value={r.count}
              max={max}
              formatted={String(r.count)}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No exits in this period.</p>
      )}
    </SectionCard>
  );
}

function LeaveSectionView({
  data,
  hasError,
}: Readonly<{
  data: AnalyticsDashboardResponse["leave_heatmap"];
  hasError: boolean;
}>) {
  const top5 = (data?.per_type ?? [])
    .slice()
    .sort((a, b) => b.days - a.days)
    .slice(0, 5);
  const max = Math.max(1, ...top5.map((r) => r.days));
  const dow = data?.day_of_week ?? [];
  const total = data?.total_leave_days ?? 0;

  return (
    <SectionCard
      title="Leave Heat Map"
      icon={HeartHandshake}
      description="Approved leave in this period, by type and weekday"
      href="/app/$tenantSlug/leave"
      hrefLabel="Leave"
      hasError={hasError}
    >
      {top5.length > 0 ? (
        <>
          <p className="mb-2 text-xs text-muted-foreground">
            {total} {total === 1 ? "day" : "days"} of approved leave
          </p>
          <div className="mb-3 space-y-0.5">
            {top5.map((r) => (
              <CSSBarRow
                key={r.leave_type_id}
                label={r.name || r.code}
                value={r.days}
                max={max}
                formatted={`${r.days}d`}
              />
            ))}
          </div>
          <CSSColumns
            data={dow.map((d) => ({
              key: d.day,
              label: d.day,
              value: d.days,
              title: `${d.day}: ${d.days} ${d.days === 1 ? "person-day" : "person-days"} on leave`,
            }))}
            highlight={data?.peak_day_of_week}
          />
        </>
      ) : (
        <p className="text-xs text-muted-foreground">
          No approved leave in this period.
        </p>
      )}
    </SectionCard>
  );
}

function TrainingSectionView({
  data,
  hasError,
}: Readonly<{
  data: AnalyticsDashboardResponse["training_compliance"];
  hasError: boolean;
}>) {
  const modules = data?.per_module ?? [];
  const threshold = data?.threshold_pct ?? 80;
  return (
    <SectionCard
      title="Training Completion"
      icon={GraduationCap}
      description="Mandatory POSH training, active employees"
      href="/app/$tenantSlug/posh/compliance"
      hrefLabel="POSH compliance"
      hasError={hasError}
    >
      <div className="mb-3">
        <p className="text-3xl font-semibold tabular-nums">
          {data?.overall_pct ?? 0}%
        </p>
        <p className="text-xs text-muted-foreground">
          Overall completion ({data?.total_completed ?? 0} of{" "}
          {data?.total_assignments ?? 0} module completions)
        </p>
      </div>
      {modules.length > 0 ? (
        <div className="space-y-0.5">
          {modules.map((m) => (
            <CSSBarRow
              key={m.module_id}
              label={m.name || m.code}
              value={m.completed_pct}
              max={100}
              formatted={`${m.completed_pct}%`}
              tone={m.completed_pct < threshold ? "warn" : "primary"}
            />
          ))}
          <p className="pt-1 text-[11px] text-muted-foreground">
            {data && data.below_threshold.length > 0
              ? `${data.below_threshold.length} ${data.below_threshold.length === 1 ? "module is" : "modules are"} below ${threshold}%.`
              : `Every module is at or above ${threshold}%.`}
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No mandatory training modules.</p>
      )}
    </SectionCard>
  );
}

function HelpdeskSectionView({
  data,
  hasError,
}: Readonly<{
  data: AnalyticsDashboardResponse["helpdesk_sla"];
  hasError: boolean;
}>) {
  const open =
    (data?.status_open ?? 0) +
    (data?.status_in_progress ?? 0) +
    (data?.status_waiting_on_user ?? 0);
  const resolved = (data?.status_resolved ?? 0) + (data?.status_closed ?? 0);
  const total = Math.max(1, open + resolved);
  const openPct = Math.round((open / total) * 100);
  const resolvedPct = open + resolved === 0 ? 0 : 100 - openPct;
  const hours = data?.avg_resolution_hours;
  return (
    <SectionCard
      title="Helpdesk"
      icon={LifeBuoy}
      description="Tickets raised in this period"
      href="/app/$tenantSlug/helpdesk"
      hrefLabel="Helpdesk"
      hasError={hasError}
    >
      <div className="mb-3">
        <p className="text-3xl font-semibold tabular-nums">
          {hours !== undefined ? `${hours}h` : "—"}
        </p>
        <p className="text-xs text-muted-foreground">
          {hours !== undefined
            ? "Average time to resolve"
            : "No tickets resolved in this period"}
        </p>
      </div>
      <div className="space-y-1.5">
        <div className="flex h-2 overflow-hidden rounded bg-muted">
          <div className="h-full bg-amber-500" style={{ width: `${openPct}%` }} />
          <div
            className="h-full bg-emerald-500"
            style={{ width: `${resolvedPct}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            <span className="text-muted-foreground">Open</span>
            <span className="ml-1 tabular-nums text-foreground">{open}</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <span className="text-muted-foreground">Resolved</span>
            <span className="ml-1 tabular-nums text-foreground">{resolved}</span>
          </span>
        </div>
      </div>
    </SectionCard>
  );
}

function SurveySectionView({
  data,
  hasError,
}: Readonly<{
  data: AnalyticsDashboardResponse["survey_participation"];
  hasError: boolean;
}>) {
  const enps = data?.latest_enps;
  const responses = (data?.active_survey_rows ?? []).reduce(
    (n, r) => n + r.response_count,
    0,
  );
  return (
    <SectionCard
      title="Surveys"
      icon={BarChart2}
      description="Employee NPS and open surveys"
      href="/app/$tenantSlug/me/surveys"
      hrefLabel="Surveys"
      hasError={hasError}
    >
      <div className="mb-3 flex items-baseline gap-3">
        <p
          className={cn(
            "text-3xl font-semibold tabular-nums",
            enps ? enpsClass(enps.score) : "text-muted-foreground",
          )}
        >
          {enps ? enps.score : "—"}
        </p>
        <p className="text-xs text-muted-foreground">
          {enps
            ? `Latest employee NPS (${enps.total_responses} responses, ${enps.compliance_pct}% took part)`
            : "No employee NPS survey closed in this period"}
        </p>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Open surveys</span>
        <span className="tabular-nums text-foreground">
          {data?.active_surveys ?? 0}
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Responses so far</span>
        <span className="tabular-nums text-foreground">{responses}</span>
      </div>
    </SectionCard>
  );
}

function LoadingGrid() {
  return (
    <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
      {SECTION_KEYS.map((k) => (
        <Skeleton key={k} className="h-56" />
      ))}
    </div>
  );
}

function BackToReports() {
  return (
    <Link
      to={REPORTS_HREF}
      className="relative mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground pointer-coarse:min-h-11"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to Reports
    </Link>
  );
}

function AnalyticsDashboardPage() {
  const { user } = useAuth();
  const allowed = isHRAdmin(user?.role);

  const [from, setFrom] = useState<string>(() => fyStart());
  const [to, setTo] = useState<string>(() => todayISO());
  const buckets: AnalyticsBucket = "monthly";
  const validRange = Boolean(from) && Boolean(to) && from <= to;

  const query = useQuery({
    queryKey: ["analytics", "dashboard", from, to, buckets] as const,
    queryFn: ({ signal }) =>
      analyticsApi.getAnalyticsDashboard(from, to, buckets, signal),
    enabled: allowed && validRange,
    staleTime: 60 * 1000,
  });

  const errorSet = useMemo(() => {
    const out = new Set<AnalyticsSectionKey>();
    for (const e of query.data?.errors ?? []) out.add(e.section);
    return out;
  }, [query.data?.errors]);

  if (!allowed) {
    return (
      <>
        <BackToReports />
        <PageHeader title="Analytics" />
        <EmptyState
          icon={BarChart2}
          title="Only HR can see analytics"
          description="Ask your company admin or HR manager for access."
        />
      </>
    );
  }

  const data = query.data;

  return (
    <>
      <BackToReports />
      <PageHeader
        title="Analytics"
        description="Headcount, attrition, leave, training, helpdesk and surveys for a date range."
      />

      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-end gap-3 py-4">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="analytics-from"
              className="text-xs font-medium text-muted-foreground"
            >
              From
            </label>
            <input
              id="analytics-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-9 pointer-coarse:h-11 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="analytics-to"
              className="text-xs font-medium text-muted-foreground"
            >
              To
            </label>
            <input
              id="analytics-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-9 pointer-coarse:h-11 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={() => {
              setFrom(fyStart());
              setTo(todayISO());
            }}
          >
            This Financial Year
          </Button>
        </CardContent>
      </Card>

      {!validRange && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            The “From” date must be on or before the “To” date.
          </AlertDescription>
        </Alert>
      )}

      {query.isError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            Couldn't load analytics. Check the dates (at most 24 months) and
            try again.
          </AlertDescription>
        </Alert>
      )}

      {validRange && query.isPending ? (
        <LoadingGrid />
      ) : (
        !query.isError && (
          <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
            <HeadcountSectionView
              data={data?.headcount}
              hasError={errorSet.has("headcount")}
            />
            <AttritionSectionView
              data={data?.attrition}
              hasError={errorSet.has("attrition")}
            />
            <LeaveSectionView
              data={data?.leave_heatmap}
              hasError={errorSet.has("leave_heatmap")}
            />
            <TrainingSectionView
              data={data?.training_compliance}
              hasError={errorSet.has("training_compliance")}
            />
            <HelpdeskSectionView
              data={data?.helpdesk_sla}
              hasError={errorSet.has("helpdesk_sla")}
            />
            <SurveySectionView
              data={data?.survey_participation}
              hasError={errorSet.has("survey_participation")}
            />
          </div>
        )
      )}
    </>
  );
}
